import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Printer, Search, Loader2, RotateCcw } from "lucide-react";
import client from "@/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const day = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export const REPORTS = {
  flight: { title: "Flight Report", statuses: ["Confirmed", "Cancelled", "Pending", "Failed"] },
  hotel: { title: "Hotel Report", statuses: ["Confirmed", "Cancelled", "Pending", "Failed"] },
  bus: { title: "Bus Report", statuses: ["Confirmed", "Cancelled", "Pending"] },
  holiday: { title: "Holiday Report", statuses: ["Confirmed", "Cancelled", "Pending"] },
  wallet: { title: "Wallet / Ledger Report", statuses: ["credit", "debit"] },
};

const BOOKING_COLS = [
  ["S.No", (r, i) => i + 1], ["Booking Date", (r) => day(r.created_at)], ["PNR / Ref", (r) => r.pnr || r.booking_id],
  ["Route / Item", (r) => r.route], ["Airline", (r) => r.airline], ["Travel Date", (r) => r.travel_date], ["Pax", (r) => r.pax],
  ["Passenger", (r) => r.passenger], ["Booked By", (r) => r.booked_by], ["Amount", (r) => inr(r.total)], ["Status", (r) => r.status], ["Payment", (r) => r.payment],
];
const WALLET_COLS = [
  ["S.No", (r, i) => i + 1], ["Date", (r) => day(r.created_at)], ["Txn ID", (r) => r.txn_id], ["Account", (r) => r.account],
  ["Email", (r) => r.email], ["Narration", (r) => r.note], ["Credit", (r) => (r.type === "credit" ? inr(r.amount) : "")],
  ["Debit", (r) => (r.type === "debit" ? inr(r.amount) : "")],
];

const Row = ({ label, children }) => (
  <div className="grid items-center gap-2 border-b border-slate-100 px-5 py-3.5 sm:grid-cols-[200px_1fr]">
    <label className="text-sm font-semibold text-slate-700">{label}</label>
    <div className="max-w-md">{children}</div>
  </div>
);
const ctl = "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-[#1B1F5B] focus:outline-none focus:ring-2 focus:ring-[#1B1F5B]/15";

const ReportForm = ({ kind, admin, agencies, f, setF, onSubmit, onReset, busy }) => (
  <form onSubmit={onSubmit} className="overflow-hidden rounded-lg border border-[#1B1F5B]/20 bg-white" data-testid={`report-form-${kind}`}>
    <div className="bg-[#1B1F5B] px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white">Search {REPORTS[kind].title}</div>
    <Row label="From Date"><input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} className={ctl} data-testid="report-from-date" /></Row>
    <Row label="To Date"><input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className={ctl} data-testid="report-to-date" /></Row>
    <Row label={kind === "wallet" ? "Transaction Type" : "Booking Status"}>
      <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={ctl} data-testid="report-status">
        <option value="">All</option>
        {REPORTS[kind].statuses.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
      </select>
    </Row>
    <Row label={kind === "wallet" ? "Txn ID / Narration" : "PNR / Booking ID"}>
      <input value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} className={ctl} placeholder="Optional" data-testid="report-ref" />
    </Row>
    {agencies && (
      <Row label="Agency">
        <select value={f.tenant} onChange={(e) => setF({ ...f, tenant: e.target.value })} className={ctl} data-testid="report-agency">
          <option value="">All agencies</option>
          <option value="platform">FLYTRIP (direct)</option>
          {agencies.map((a) => <option key={a.tenant_id} value={a.tenant_id}>{a.name}</option>)}
        </select>
      </Row>
    )}
    {admin && (
      <Row label="Agent / User Email"><input type="email" value={f.agent} onChange={(e) => setF({ ...f, agent: e.target.value })} className={ctl} placeholder="Optional — all users" data-testid="report-agent" /></Row>
    )}
    <div className="flex gap-3 bg-slate-50 px-5 py-4 sm:pl-[220px]">
      <button type="submit" disabled={busy} data-testid="report-submit-btn"
        className="flex items-center gap-2 rounded-md bg-[#0E7C6B] px-8 py-2.5 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-[#0A5348] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Submit
      </button>
      <button type="button" onClick={onReset} data-testid="report-reset-btn"
        className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
        <RotateCcw className="h-4 w-4" /> Reset
      </button>
    </div>
  </form>
);

const exportXls = (title, cols, rows) => {
  const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const html = `<table border="1"><tr>${cols.map((c) => `<th>${esc(c[0])}</th>`).join("")}</tr>${rows
    .map((r, i) => `<tr>${cols.map((c) => `<td>${esc(c[1](r, i))}</td>`).join("")}</tr>`).join("")}</table>`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([`\ufeff${html}`], { type: "application/vnd.ms-excel" }));
  a.download = `${title.replace(/\W+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xls`;
  a.click();
};

const printTable = (title, node) => {
  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) return toast.error("Allow pop-ups to print");
  w.document.write(`<html><head><title>${title}</title><style>body{font-family:Arial;padding:16px}h2{margin:0 0 12px}table{border-collapse:collapse;width:100%;font-size:11px}th,td{border:1px solid #999;padding:5px;text-align:left}th{background:#1B1F5B;color:#fff}</style></head><body><h2>${title}</h2>${node.innerHTML}</body></html>`);
  w.document.close(); w.focus(); w.print();
};

const Results = ({ kind, data }) => {
  const ref = useRef(null);
  const cols = kind === "wallet" ? WALLET_COLS : BOOKING_COLS;
  const s = data.summary || {};
  const title = REPORTS[kind].title;
  return (
    <div className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid={`report-results-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-slate-50 px-5 py-3">
        <p className="text-sm text-slate-600" data-testid="report-summary">
          <b className="text-[#1B1F5B]">{s.count || 0}</b> records
          {kind === "wallet" ? <> · Credit <b className="text-[#0E7C6B]">{inr(s.credit)}</b> · Debit <b className="text-red-600">{inr(s.debit)}</b></>
            : <> · Total <b className="text-[#1B1F5B]">{inr(s.amount)}</b></>}
        </p>
        <div className="flex gap-2">
          <button onClick={() => exportXls(title, cols, data.rows)} disabled={!data.rows.length} data-testid="report-excel-btn"
            className="flex items-center gap-1.5 rounded-md bg-[#107C41] px-3.5 py-2 text-xs font-bold text-white hover:opacity-90 disabled:opacity-40"><FileSpreadsheet className="h-4 w-4" />Excel</button>
          <button onClick={() => printTable(title, ref.current)} disabled={!data.rows.length} data-testid="report-print-btn"
            className="flex items-center gap-1.5 rounded-md bg-[#1B1F5B] px-3.5 py-2 text-xs font-bold text-white hover:opacity-90 disabled:opacity-40"><Printer className="h-4 w-4" />Print</button>
        </div>
      </div>
      <div ref={ref} className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-[13px]" data-testid="report-table">
          <thead><tr className="bg-[#1B1F5B] text-left text-white">{cols.map((c) => <th key={c[0]} className="whitespace-nowrap px-3 py-2.5 font-semibold">{c[0]}</th>)}</tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={cols.length} className="py-10 text-center text-slate-400" data-testid="report-no-records">No records found</td></tr>}
            {data.rows.map((r, i) => (
              <tr key={r.booking_id || r.txn_id || i} className="border-b border-slate-100 odd:bg-white even:bg-slate-50/60">
                {cols.map((c) => <td key={c[0]} className="whitespace-nowrap px-3 py-2">{c[1](r, i)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const blank = { frm: "", to: "", status: "", ref: "", agent: "", tenant: "" };

export const ReportCenter = ({ kind, admin, platform }) => {
  const [agencies, setAgencies] = useState(null);
  useEffect(() => { if (platform) client.get("/admin/tenants").then((r) => setAgencies(r.data.tenants)).catch(() => {}); }, [platform]);
  const [f, setF] = useState(blank);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.frm && f.to && f.frm > f.to) return toast.error("From Date cannot be after To Date");
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries({ kind, ...f }).filter(([, v]) => v));
      setData((await client.get("/reports/search", { params })).data);
    } catch (x) { toast.error(x?.response?.data?.detail || "Could not load report"); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`report-center-${kind}`} key={kind}>
      <h2 className="mb-4 text-xl font-bold text-[#1B1F5B]">{REPORTS[kind].title}</h2>
      <ReportForm kind={kind} admin={admin} agencies={agencies} f={f} setF={setF} busy={busy} onSubmit={submit} onReset={() => { setF(blank); setData(null); }} />
      {data && <Results kind={kind} data={data} />}
    </div>
  );
};

export default ReportCenter;
